import { useState } from "react"
import { Search, X, SlidersHorizontal } from "lucide-react"
import en from "../i18n/en"
import { MultiSelect } from "./MultiSelect"

const defaultChipLabel = (field, value, options) => {
  if (field.type === "checkbox") return field.label
  if (field.type === "multiselect") {
    const ids = String(value).split(",").filter(Boolean)
    if (ids.length === 1) {
      const opt = (options || []).find((o) => String(o.value) === ids[0])
      return `${field.label}: ${opt ? opt.label : ids[0]}`
    }
    return `${field.label}: ${en.common.selected(ids.length)}`
  }
  const opt = (options || []).find((o) => String(o.value) === String(value))
  return opt ? `${field.label}: ${opt.label}` : `${field.label}: ${value}`
}

// A free-text search field stays visible on mobile; every other control
// collapses behind the "Filters" toggle so the mobile user isn't scrolling a
// tall stack of inputs.
const isSearchField = (f) => !f.type || f.type === "search" || f.key === "search"

export const FilterBar = ({
  fields = [],
  values = {},
  onChange,
  onReset,
  title,
  extra,
  children,
}) => {
  const [open, setOpen] = useState(false)
  const active = fields.filter((f) => {
    const v = values[f.key]
    return v !== undefined && v !== null && v !== "" && v !== f.defaultValue
  })
  const hasActive = active.length > 0

  const renderControl = (f) => {
    if (f.type === "checkbox") {
      // value "1" = on, "" = off, so it clears like every other filter.
      return (
        <label className="pc-checkbox" style={{ marginTop: 26 }}>
          <input
            type="checkbox"
            checked={values[f.key] === "1"}
            onChange={(e) => onChange?.({ [f.key]: e.target.checked ? "1" : "" })}
          />
          <span>{f.label}</span>
        </label>
      )
    }
    if (f.type === "multiselect") {
      return (
        <MultiSelect
          options={f.options || []}
          value={(values[f.key] ?? "").split(",").filter(Boolean)}
          disabled={f.disabled}
          loading={f.loading}
          placeholder={f.placeholder || "All"}
          searchPlaceholder={f.searchPlaceholder}
          onChange={(next) => onChange?.({ [f.key]: next.join(",") })}
        />
      )
    }
    if (f.type === "select") {
      return (
        <select
          className="pc-control"
          value={values[f.key] ?? ""}
          disabled={f.disabled}
          title={f.disabledTitle}
          onChange={(e) => onChange?.({ [f.key]: e.target.value })}
        >
          {f.allowEmpty !== false && <option value="">{f.placeholder || "All"}</option>}
          {(f.options || []).map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )
    }
    if (f.type === "date") {
      return (
        <input
          className="pc-control"
          type="date"
          value={values[f.key] ?? ""}
          disabled={f.disabled}
          title={f.disabledTitle}
          onChange={(e) => onChange?.({ [f.key]: e.target.value })}
        />
      )
    }
    return (
      <span className="ds-search-wrap">
        <Search aria-hidden />
        <input
          className="pc-control"
          type="search"
          value={values[f.key] ?? ""}
          placeholder={f.placeholder || ""}
          onChange={(e) => onChange?.({ [f.key]: e.target.value })}
        />
      </span>
    )
  }

  return (
    <div className={`ds-filters${open ? " is-open" : ""}`}>
      <div className="ds-filters-head">
        <span className="ds-filters-title">
          <SlidersHorizontal aria-hidden />
          {title || en.filter.showFilters}
        </span>
        <div className="ds-filters-head-actions">
          {extra}
          <button
            type="button"
            className="pc-btn pc-btn--secondary pc-btn--sm ds-filters-toggle"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {en.filter.showFilters}
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--secondary pc-btn--sm"
            disabled={!hasActive}
            onClick={onReset}
          >
            {en.common.reset}
          </button>
        </div>
      </div>

      <div className="ds-filters-grid">
        {fields.map((f) => (
          <div
            key={f.key}
            className={`pc-field${isSearchField(f) ? "" : " ds-filter-advanced"}`}
          >
            {f.label && f.type !== "checkbox" && <label className="pc-label">{f.label}</label>}
            {renderControl(f)}
          </div>
        ))}
        {children}
      </div>

      {hasActive && (
        <div className="ds-chips">
          {active.map((f) => (
            <span key={f.key} className="ds-chip">
              {defaultChipLabel(f, values[f.key], f.options)}
              <button
                type="button"
                aria-label={`Remove ${f.label}`}
                onClick={() => onChange?.({ [f.key]: f.defaultValue ?? "" })}
              >
                <X size={12} aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default FilterBar
